import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { PaginatedResponse } from '@/config/api';
import type {
  Product,
  ProductCategory,
  CatalogFilters,
  CatalogFilterOptions,
  Cart,
  Order,
  TrackingInfo,
  Invoice,
  Payment,
  CreditMemo,
  AccountStatement,
  CustomerProfile,
  Address,
  CustomerPreferences,
  Promotion,
  SpendingSummary,
  Notification,
  ProductLabelData,
  ExceptionItem,
  ExceptionSummary,
  JournalEntryFull,
  FixedAsset,
  BudgetItem,
  BudgetVariance,
  CurrencyRate,
  FiscalPeriod,
  SavedPaymentMethod,
  DiscountEligibility,
  PaymentPlan,
  InvoiceNotificationPreferences,
  EnhancedTrackingInfo,
  ProofOfDeliveryData,
  OrderModification,
  RecurringOrder,
  CreditSummary,
  CreditApplicationData,
  ExemptionCertificateData,
  ContractDetail,
  FuturePrice,
  SupportTicketSummary,
  SupportTicketDetail,
  CustomerDoc,
  DocCategory,
  TeamMember,
  OrderTemplate,
  ParLevelConfig,
  ParCheckResult,
  DeliveryCalendarEvent,
  DeliveryFeedbackData,
  QualityTrend,
  ClaimSummary,
  ClaimDetail,
  SubstitutionPref,
  SubstitutionHistoryItem,
  PriceWatch,
  PriceWatchAlertItem,
  VolumeTier,
  DiscountNudge,
  PriceComparisonData,
  LocationSummary,
  ConsolidatedSpend,
  SeasonalCalendarEntry,
  SourcingWindowInfo,
  ShortageAlertData,
  LiveTrackingData,
  SuggestedItem,
  OrderPatternItem,
  SubUser,
  PendingApproval,
  Dispute,
  PaymentSchedule,
  CRMContact,
  PortalCreditApplication,
  EmploymentApplication,
  NetTermsChangeRequest,
  StatementCycleChangeRequest,
} from '@/types';
import { useCartStore } from '@/store/cart';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function fetchOne<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const { data } = await api.get<T>(url, { params });
  return data;
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export function useAuthMe() {
  return useQuery({
    queryKey: ['auth-me'],
    queryFn: () => fetchOne<{ user_id: number; username: string; role: string; permissions: string[]; customer: unknown }>('/auth/me'),
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Products / Catalog
// ---------------------------------------------------------------------------

export function useProductCategories() {
  return useQuery({
    queryKey: ['product-categories'],
    queryFn: () => fetchOne<ProductCategory[]>('/catalog/categories'),
  });
}

export function useProducts(params?: CatalogFilters) {
  return useQuery({
    queryKey: ['portal-products', params],
    queryFn: () => fetchOne<PaginatedResponse<Product>>('/catalog/products', params as Record<string, unknown>),
  });
}

export function useFilterOptions() {
  return useQuery({
    queryKey: ['catalog-filter-options'],
    queryFn: () => fetchOne<CatalogFilterOptions>('/catalog/filter-options'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useFeaturedProducts() {
  return useQuery({
    queryKey: ['featured-products'],
    queryFn: () => fetchOne<Product[]>('/catalog/featured'),
  });
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export function useProductLabelData(
  productId: number,
  params?: { lot_id?: number; barcode_type?: string },
) {
  return useQuery({
    queryKey: ['product-label', productId, params],
    queryFn: () =>
      fetchOne<ProductLabelData>(
        `/catalog/products/${productId}/label-data`,
        params as Record<string, unknown>,
      ),
    enabled: productId > 0,
  });
}

// ---------------------------------------------------------------------------
// Cart
// ---------------------------------------------------------------------------

export function useCart() {
  const setItemCount = useCartStore.getState().setItemCount;
  return useQuery({
    queryKey: ['cart'],
    queryFn: async () => {
      const data = await fetchOne<Cart>('/cart');
      setItemCount(data.item_count);
      return data;
    },
  });
}

export function useAddToCart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (item: { product_id: number; quantity: number; unit?: string; notes?: string }) =>
      api.post('/cart/items', item),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cart'] }),
  });
}

export function useUpdateCartItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, quantity }: { itemId: number; quantity: number }) =>
      api.patch(`/cart/items/${itemId}`, { quantity }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cart'] }),
  });
}

export function useRemoveCartItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: number) => api.delete(`/cart/items/${itemId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cart'] }),
  });
}

export function useClearCart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete('/cart'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cart'] }),
  });
}

export function useApplyPromo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.post('/cart/promo', { code }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cart'] }),
  });
}

export function useCheckout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: {
      delivery_date?: string;
      delivery_address_id?: number;
      payment_method?: string;
      po_number?: string;
      notes?: string;
    }) => api.post('/cart/checkout', req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cart'] });
      qc.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Orders
// ---------------------------------------------------------------------------

export function useOrders(params?: { status?: string; limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['orders', params],
    queryFn: () => fetchOne<{ items: Order[]; total: number }>('/orders', params),
  });
}

export function useOrder(id: number) {
  return useQuery({
    queryKey: ['orders', id],
    queryFn: () => fetchOne<Order>(`/orders/${id}`),
    enabled: id > 0,
  });
}

export function useReorder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: number) => api.post(`/orders/${orderId}/reorder`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });
}

export function useCancelOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, reason }: { orderId: number; reason?: string }) =>
      api.post(`/orders/${orderId}/cancel`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });
}

// ---------------------------------------------------------------------------
// Tracking
// ---------------------------------------------------------------------------

export function useTracking(orderId: number) {
  return useQuery({
    queryKey: ['tracking', orderId],
    queryFn: () => fetchOne<TrackingInfo>(`/tracking/track/${orderId}`),
    enabled: orderId > 0,
  });
}

// ---------------------------------------------------------------------------
// Invoices / Payments
// ---------------------------------------------------------------------------

export function useInvoices(params?: { status?: string; limit?: number; offset?: number; search?: string; date_from?: string; date_to?: string }) {
  return useQuery({
    queryKey: ['invoices', params],
    queryFn: () => fetchOne<{ items: Invoice[]; total: number }>('/payments/invoices', params),
  });
}

export function useInvoice(id: number) {
  return useQuery({
    queryKey: ['invoices', id],
    queryFn: () => fetchOne<Invoice>(`/payments/invoices/${id}`),
    enabled: id > 0,
  });
}

export function usePaymentIntent() {
  return useMutation({
    mutationFn: (invoiceId: number) => api.post('/payments/intent', { invoice_id: invoiceId }),
  });
}

export function usePayments(params?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['payments', params],
    queryFn: () => fetchOne<{ items: Payment[]; total: number }>('/payments', params),
  });
}

export function useMakePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      invoice_ids: number[];
      amounts: Record<number, number>;
      payment_method: string;
      reference?: string;
    }) => api.post('/payments/pay', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['payments'] });
    },
  });
}

export function useSubmitDispute() {
  return useMutation({
    mutationFn: (data: { invoice_id: number; reason: string; details: string }) =>
      api.post('/payments/disputes', data),
  });
}

export function useDisputes(params?: { status?: string }) {
  return useQuery({
    queryKey: ['disputes', params],
    queryFn: () => fetchOne<{ items: Dispute[]; total: number }>('/ar-disputes', params),
  });
}

export function useDispute(id: number) {
  return useQuery({
    queryKey: ['disputes', id],
    queryFn: () => fetchOne<Dispute>(`/ar-disputes/${id}`),
    enabled: id > 0,
  });
}

export function usePaymentSchedules() {
  return useQuery({
    queryKey: ['payment-schedules'],
    queryFn: () => fetchOne<PaymentSchedule[]>('/autopay/enrollments'),
  });
}

export function useCreatePaymentSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { frequency: string; payment_method: string; max_amount?: number }) =>
      api.post('/autopay/enrollments', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-schedules'] }),
  });
}

export function useUpdatePaymentSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; frequency?: string; payment_method?: string; max_amount?: number; is_active?: boolean }) =>
      api.put(`/autopay/enrollments/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-schedules'] }),
  });
}

export function useDeletePaymentSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/autopay/enrollments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-schedules'] }),
  });
}

export function useCreditMemos(params?: { status?: string; limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['credit-memos', params],
    queryFn: () => fetchOne<{ items: CreditMemo[]; total: number }>('/payments/credits', params),
  });
}

export function useApplyCreditMemo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { credit_id: number; invoice_id: number; amount: number }) =>
      api.post('/payments/credits/apply', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credit-memos'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useAccountStatement(params?: { start_date?: string; end_date?: string }) {
  return useQuery({
    queryKey: ['account-statement', params],
    queryFn: () => fetchOne<AccountStatement>('/payments/statement', params),
    enabled: !!params?.start_date && !!params?.end_date,
  });
}

export function useDownloadInvoicePdf() {
  return useMutation({
    mutationFn: async (invoiceId: number) => {
      const { data } = await api.get(`/payments/invoices/${invoiceId}/pdf`, {
        responseType: 'blob',
      });
      return data;
    },
  });
}

export function useDownloadStatementPdf() {
  return useMutation({
    mutationFn: async (params: { start_date: string; end_date: string }) => {
      const { data } = await api.get('/payments/statement/pdf', {
        params,
        responseType: 'blob',
      });
      return data;
    },
  });
}

export function useDownloadPaymentReceipt() {
  return useMutation({
    mutationFn: async (paymentId: number) => {
      const { data } = await api.get(`/payments/${paymentId}/receipt`, {
        responseType: 'blob',
      });
      return data;
    },
  });
}

// ---------------------------------------------------------------------------
// Customer Profile
// ---------------------------------------------------------------------------

export function useCustomerProfile() {
  return useQuery({
    queryKey: ['customer-profile'],
    queryFn: () => fetchOne<CustomerProfile>('/customers/me'),
  });
}

export function useUpdateCustomerProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<CustomerProfile>) => api.patch('/customers/me', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer-profile'] }),
  });
}

// ---------------------------------------------------------------------------
// Addresses
// ---------------------------------------------------------------------------

export function useAddresses() {
  return useQuery({
    queryKey: ['addresses'],
    queryFn: () => fetchOne<Address[]>('/customers/me/addresses'),
  });
}

export function useCreateAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<Address, 'id'>) => api.post('/customers/me/addresses', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addresses'] }),
  });
}

// ---------------------------------------------------------------------------
// Preferences
// ---------------------------------------------------------------------------

export function useCustomerPreferences() {
  return useQuery({
    queryKey: ['preferences'],
    queryFn: () => fetchOne<CustomerPreferences>('/customers/me/preferences'),
  });
}

export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<CustomerPreferences>) =>
      api.patch('/customers/me/preferences', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['preferences'] }),
  });
}

// ---------------------------------------------------------------------------
// Promotions
// ---------------------------------------------------------------------------

export function usePromotions() {
  return useQuery({
    queryKey: ['promotions'],
    queryFn: () => fetchOne<Promotion[]>('/promotions'),
  });
}

// ---------------------------------------------------------------------------
// Analytics / Spending
// ---------------------------------------------------------------------------

export function useSpendingSummary() {
  return useQuery({
    queryKey: ['spending-summary'],
    queryFn: () => fetchOne<SpendingSummary>('/analytics/my-spending'),
  });
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: () => fetchOne<Notification[]>('/notifications'),
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.patch(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

// ---------------------------------------------------------------------------
// Exception Queue
// ---------------------------------------------------------------------------

export function useExceptions(params?: {
  status?: string;
  severity?: string;
  exception_type?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['exceptions', params],
    queryFn: () => fetchOne<{ items: ExceptionItem[]; total: number }>('/exceptions/', params),
  });
}

export function useExceptionSummary() {
  return useQuery({
    queryKey: ['exception-summary'],
    queryFn: () => fetchOne<ExceptionSummary>('/exceptions/summary'),
  });
}

export function useAcknowledgeException() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/exceptions/${id}/acknowledge`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['exceptions'] });
      qc.invalidateQueries({ queryKey: ['exception-summary'] });
    },
  });
}

export function useResolveException() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: number; notes: string }) =>
      api.post(`/exceptions/${id}/resolve`, { notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['exceptions'] });
      qc.invalidateQueries({ queryKey: ['exception-summary'] });
    },
  });
}

export function useDismissException() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: number; notes: string }) =>
      api.post(`/exceptions/${id}/dismiss`, { notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['exceptions'] });
      qc.invalidateQueries({ queryKey: ['exception-summary'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Accounting — Journal Entries
// ---------------------------------------------------------------------------

export function useJournalEntries(params?: {
  approval_status?: string;
  status?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['journal-entries', params],
    queryFn: () => fetchOne<{ items: JournalEntryFull[]; total: number }>('/accounting/gl/entries', params),
  });
}

export function useJournalEntryDetail(id: number) {
  return useQuery({
    queryKey: ['journal-entries', id],
    queryFn: () => fetchOne<JournalEntryFull>(`/accounting/gl/entries/${id}`),
    enabled: id > 0,
  });
}

export function useCreateDraftJE() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      entry_date: string;
      memo: string;
      fiscal_period?: string;
      lines: { account_number: string; debit: number; credit: number; memo?: string; department?: string }[];
    }) => api.post('/accounting/gl/draft-journal-entry', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['journal-entries'] }),
  });
}

export function useSubmitJE() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/accounting/gl/entries/${id}/submit`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['journal-entries'] }),
  });
}

export function useApproveJE() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: number; notes?: string }) =>
      api.post(`/accounting/gl/entries/${id}/approve`, { notes }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['journal-entries'] }),
  });
}

export function useRejectJE() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      api.post(`/accounting/gl/entries/${id}/reject`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['journal-entries'] }),
  });
}

// ---------------------------------------------------------------------------
// Accounting — Fixed Assets
// ---------------------------------------------------------------------------

export function useFixedAssets(params?: {
  search?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['fixed-assets', params],
    queryFn: () => fetchOne<{ items: FixedAsset[]; total: number }>('/accounting/assets', params),
  });
}

export function useCreateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      asset_number: string;
      name: string;
      category?: string;
      acquisition_date?: string;
      acquisition_cost: number;
      useful_life_months: number;
      salvage_value?: number;
      depreciation_method?: string;
      location?: string;
    }) => api.post('/accounting/assets', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fixed-assets'] }),
  });
}

export function useDepreciateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/accounting/assets/${id}/depreciate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fixed-assets'] }),
  });
}

export function useDisposeAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: number; proceeds: number; disposal_date?: string }) =>
      api.post(`/accounting/assets/${data.id}/dispose`, {
        proceeds: data.proceeds,
        disposal_date: data.disposal_date,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fixed-assets'] }),
  });
}

// ---------------------------------------------------------------------------
// Accounting — Budgets
// ---------------------------------------------------------------------------

export function useBudgets(params?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['budgets', params],
    queryFn: () => fetchOne<{ items: BudgetItem[]; total: number }>('/accounting/budgets', params),
  });
}

export function useCreateBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; fiscal_year: number }) =>
      api.post('/accounting/budgets', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['budgets'] }),
  });
}

export function useBudgetVariance(budgetId: number) {
  return useQuery({
    queryKey: ['budgets', budgetId, 'variance'],
    queryFn: () => fetchOne<{ items: BudgetVariance[] }>(`/accounting/budgets/${budgetId}/variance`),
    enabled: budgetId > 0,
  });
}

// ---------------------------------------------------------------------------
// Accounting — Currency Rates
// ---------------------------------------------------------------------------

export function useCurrencyRates() {
  return useQuery({
    queryKey: ['currency-rates'],
    queryFn: () => fetchOne<CurrencyRate[]>('/accounting/currency/rates'),
  });
}

export function useConvertCurrency() {
  return useMutation({
    mutationFn: (data: { from_currency: string; to_currency: string; amount: number }) =>
      api.post<{ result: number; rate: number }>('/accounting/currency/convert', data),
  });
}

// ---------------------------------------------------------------------------
// Accounting — Fiscal Periods
// ---------------------------------------------------------------------------

export function useFiscalPeriods() {
  return useQuery({
    queryKey: ['fiscal-periods'],
    queryFn: () => fetchOne<FiscalPeriod[]>('/accounting/periods'),
  });
}

// ---------------------------------------------------------------------------
// Accounting — Tax Compliance
// ---------------------------------------------------------------------------

/* eslint-disable @typescript-eslint/no-explicit-any */
export function useExpiringW9Vendors(daysAhead: number) {
  return useQuery({
    queryKey: ['tax', 'w9-expiring', daysAhead],
    queryFn: () => fetchOne<any[]>(`/accounting/tax/vendors/expiring-w9?days_ahead=${daysAhead}`),
  });
}

export function use1099Readiness(taxYear: number) {
  return useQuery({
    queryKey: ['tax', '1099-readiness', taxYear],
    queryFn: () => fetchOne<any>(`/accounting/tax/1099/readiness/${taxYear}`),
  });
}
/* eslint-enable @typescript-eslint/no-explicit-any */

// ---------------------------------------------------------------------------
// Chargebacks
// ---------------------------------------------------------------------------
export function useChargebacks(params?: Record<string, any>) {
  return useQuery({ queryKey: ['chargebacks', params], queryFn: () => fetchOne<{ items: any[]; total: number }>('/chargebacks', params) });
}

// Payment Links
export function usePaymentLinks(params?: Record<string, any>) {
  return useQuery({ queryKey: ['payment-links', params], queryFn: () => fetchOne<{ items: any[]; total: number }>('/payment-links', params) });
}

// Recurring Orders
export function useRecurringOrders(params?: Record<string, any>) {
  return useQuery({ queryKey: ['recurring-orders', params], queryFn: () => fetchOne<{ items: any[]; total: number }>('/recurring-orders', params) });
}

// Substitutions
export function useSubstitutionRules(params?: Record<string, any>) {
  return useQuery({ queryKey: ['substitution-rules', params], queryFn: () => fetchOne<{ items: any[]; total: number }>('/substitutions', params) });
}

// Cold Chain
export function useColdChainRecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['cold-chain', params], queryFn: () => fetchOne<{ items: any[]; total: number }>('/compliance/cold-chain', params) });
}

// Trace
export function useTraceRecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['trace-records', params], queryFn: () => fetchOne<{ items: any[]; total: number }>('/trace/records', params) });
}

// Organic Certificates
export function useOrganicCertificates(params?: Record<string, any>) {
  return useQuery({ queryKey: ['organic-certs', params], queryFn: () => fetchOne<{ items: any[]; total: number }>('/compliance/organic/certifications', params) });
}

// COA Certificates
export function useCOACertificates(params?: Record<string, any>) {
  return useQuery({ queryKey: ['coa-certs', params], queryFn: () => fetchOne<{ items: any[]; total: number }>('/compliance/pesticide/coa-certificates', params) });
}

// COOL Records
export function useCOOLRecords(params?: Record<string, any>) {
  return useQuery({ queryKey: ['cool-records', params], queryFn: () => fetchOne<{ items: any[]; total: number }>('/cool/records', params) });
}

// Petty Cash
export function usePettyCashBoxes(params?: Record<string, any>) {
  return useQuery({ queryKey: ['petty-cash', params], queryFn: () => fetchOne<{ items: any[]; total: number }>('/petty-cash', params) });
}

// AP Aging
export function useAPAgingReport(params?: Record<string, any>) {
  return useQuery({ queryKey: ['ap-aging', params], queryFn: () => fetchOne<any>('/ap-aging/snapshots', params) });
}

// Cash Forecast
export function useCashForecast(params?: Record<string, any>) {
  return useQuery({ queryKey: ['cash-forecast', params], queryFn: () => fetchOne<any>('/cash-forecast', params) });
}

// Loyalty
export function useLoyaltyPoints(params?: Record<string, any>) {
  return useQuery({ queryKey: ['loyalty-points', params], queryFn: () => fetchOne<any>('/loyalty/my-points', params) });
}

// Sustainability
export function useSustainabilityScore(params?: Record<string, any>) {
  return useQuery({ queryKey: ['sustainability', params], queryFn: () => fetchOne<any>('/sustainability/my-score', params) });
}

// Market Data
export function useMarketData(params?: Record<string, any>) {
  return useQuery({ queryKey: ['market-data', params], queryFn: () => fetchOne<{ items: any[]; total: number }>('/market-intelligence/data', params) });
}

// Contracts
export function useCustomerContracts(params?: Record<string, any>) {
  return useQuery({ queryKey: ['customer-contracts', params], queryFn: () => fetchOne<{ items: any[]; total: number }>('/contracts/my-contracts', params) });
}

// Integrations
export function useCustomerIntegrations(params?: Record<string, any>) {
  return useQuery({ queryKey: ['customer-integrations', params], queryFn: () => fetchOne<{ items: any[]; total: number }>('/integrations/my-integrations', params) });
}

// ---------------------------------------------------------------------------
// Quick Reorder — Recent Order Items
// ---------------------------------------------------------------------------

export interface RecentOrderItem {
  id: number;
  product_id: number;
  product_name: string;
  sku: string;
  last_ordered_date: string;
  last_quantity: number;
  unit_price: string;
  order_count: number;
}

export function useRecentOrderItems(limit = 50) {
  return useQuery({
    queryKey: ['recent-order-items', limit],
    queryFn: () => fetchOne<{ items: RecentOrderItem[] }>('/orders/recent-items', { limit }),
  });
}

// ---------------------------------------------------------------------------
// Saved Payment Methods
// ---------------------------------------------------------------------------

export function useSavedPaymentMethods() {
  return useQuery({
    queryKey: ['payment-methods'],
    queryFn: () => fetchOne<SavedPaymentMethod[]>('/payments/methods'),
  });
}

// ---------------------------------------------------------------------------
// AI Assistant
// ---------------------------------------------------------------------------

export function useAIChatSend() {
  return useMutation({
    mutationFn: (data: { message: string; session_id?: string; customer_id: number }) =>
      api.post('/ai-customer-assistant/chat', data),
  });
}

export function useAISmartReorder(customerId: number) {
  return useQuery({
    queryKey: ['ai-smart-reorder', customerId],
    queryFn: () => fetchOne<any>(`/ai-customer-assistant/smart-reorder/${customerId}`),
    enabled: customerId > 0,
  });
}

export function useAIRecommendations(customerId: number) {
  return useQuery({
    queryKey: ['ai-recommendations', customerId],
    queryFn: () => fetchOne<any>(`/ai-customer-assistant/recommendations/${customerId}`),
    enabled: customerId > 0,
  });
}

// ---------------------------------------------------------------------------
// Invoice Self-Service
// ---------------------------------------------------------------------------

export function useEmailInvoiceCopy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: number) => api.post(`/payments/invoices/${invoiceId}/email-copy`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
  });
}

export function useBatchDownloadInvoices() {
  return useMutation({
    mutationFn: async (invoiceIds: number[]) => {
      const { data } = await api.post('/payments/invoices/batch-download', { invoice_ids: invoiceIds }, { responseType: 'blob' });
      return data;
    },
  });
}

export function useDiscountEligibility(invoiceId: number) {
  return useQuery({
    queryKey: ['discount-eligibility', invoiceId],
    queryFn: () => fetchOne<DiscountEligibility>(`/payments/invoices/${invoiceId}/discount-eligibility`),
    enabled: invoiceId > 0,
  });
}

export function usePaymentPlans(status?: string) {
  return useQuery({
    queryKey: ['payment-plans', status],
    queryFn: () => fetchOne<{ items: PaymentPlan[]; total: number }>('/payments/plans', status ? { status } : undefined),
  });
}

export function usePaymentPlan(planId: number) {
  return useQuery({
    queryKey: ['payment-plans', planId],
    queryFn: () => fetchOne<PaymentPlan>(`/payments/plans/${planId}`),
    enabled: planId > 0,
  });
}

export function useCreatePaymentPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { invoice_id?: number; total_amount: string; num_installments: number; frequency: string; start_date?: string }) =>
      api.post('/payments/plans', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-plans'] }),
  });
}

export function useCancelPaymentPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planId: number) => api.post(`/payments/plans/${planId}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-plans'] }),
  });
}

export function useInvoiceNotificationPrefs() {
  return useQuery({
    queryKey: ['notification-prefs'],
    queryFn: () => fetchOne<InvoiceNotificationPreferences>('/payments/notification-preferences'),
  });
}

export function useUpdateInvoiceNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<InvoiceNotificationPreferences>) =>
      api.put('/payments/notification-preferences', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-prefs'] }),
  });
}

// ---------------------------------------------------------------------------
// Enhanced Tracking / POD
// ---------------------------------------------------------------------------

export function useEnhancedTracking(orderId: number) {
  return useQuery({
    queryKey: ['enhanced-tracking', orderId],
    queryFn: () => fetchOne<EnhancedTrackingInfo>(`/tracking/track/${orderId}/enhanced`),
    enabled: orderId > 0,
  });
}

export function useProofOfDelivery(orderId: number) {
  return useQuery({
    queryKey: ['pod', orderId],
    queryFn: () => fetchOne<ProofOfDeliveryData>(`/tracking/track/${orderId}/pod`),
    enabled: orderId > 0,
  });
}

export function useShareTrackingLink() {
  return useMutation({
    mutationFn: (orderId: number) =>
      api.post<{ tracking_url: string }>(`/tracking/track/${orderId}/share`),
  });
}

// ---------------------------------------------------------------------------
// Order Modifications
// ---------------------------------------------------------------------------

export function useSubmitOrderModification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, ...data }: { orderId: number; modifications: Record<string, unknown>[]; notes?: string }) =>
      api.post(`/orders/${orderId}/modify`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['order-modifications'] });
    },
  });
}

export function useOrderModifications(orderId: number) {
  return useQuery({
    queryKey: ['order-modifications', orderId],
    queryFn: () => fetchOne<OrderModification[]>(`/orders/${orderId}/modifications`),
    enabled: orderId > 0,
  });
}

export function useOrderModifyCutoff(orderId: number) {
  return useQuery({
    queryKey: ['order-modify-cutoff', orderId],
    queryFn: () => fetchOne<{ cutoff_at: string | null; can_modify: boolean; reason: string }>(`/orders/${orderId}/modify/cutoff`),
    enabled: orderId > 0,
  });
}

export function useCancelModification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, requestId }: { orderId: number; requestId: number }) =>
      api.delete(`/orders/${orderId}/modifications/${requestId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order-modifications'] }),
  });
}

// ---------------------------------------------------------------------------
// Recurring Orders (typed)
// ---------------------------------------------------------------------------

export function useMyRecurringOrders(params?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['my-recurring-orders', params],
    queryFn: () => fetchOne<{ items: RecurringOrder[]; total: number }>('/recurring-orders/mine', params),
  });
}

export function useRecurringOrder(id: number) {
  return useQuery({
    queryKey: ['recurring-order', id],
    queryFn: () => fetchOne<RecurringOrder>(`/recurring-orders/${id}`),
    enabled: id > 0,
  });
}

export function useCreateRecurringOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; frequency: string; day_of_week?: number; day_of_month?: number; auto_confirm?: boolean; items: { product_id: number; quantity: number }[] }) =>
      api.post('/recurring-orders', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-recurring-orders'] }),
  });
}

export function useUpdateRecurringOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; name?: string; frequency?: string; day_of_week?: number; day_of_month?: number; auto_confirm?: boolean }) =>
      api.patch(`/recurring-orders/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-recurring-orders'] });
      qc.invalidateQueries({ queryKey: ['recurring-order'] });
    },
  });
}

export function usePauseRecurringOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/recurring-orders/${id}/pause`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-recurring-orders'] }),
  });
}

export function useResumeRecurringOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/recurring-orders/${id}/resume`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-recurring-orders'] }),
  });
}

export function useAddRecurringOrderItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; product_id: number; quantity: number }) =>
      api.post(`/recurring-orders/${id}/items`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recurring-order'] }),
  });
}

export function useRemoveRecurringOrderItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, itemId }: { id: number; itemId: number }) =>
      api.delete(`/recurring-orders/${id}/items/${itemId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recurring-order'] }),
  });
}

export function useRecurringOrderHistory(id: number) {
  return useQuery({
    queryKey: ['recurring-order-history', id],
    queryFn: () => fetchOne<{ items: { id: number; generated_at: string; order_id: number | null; status: string }[] }>(`/recurring-orders/${id}/history`),
    enabled: id > 0,
  });
}

// ---------------------------------------------------------------------------
// Statement Generation
// ---------------------------------------------------------------------------

export function useGenerateStatement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { statement_type: string; start_date?: string; end_date?: string }) =>
      api.post('/payments/statement/generate', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['statement-history'] }),
  });
}

export function useDownloadGeneratedStatementPdf() {
  return useMutation({
    mutationFn: async (statementId: number) => {
      const { data } = await api.get(`/payments/statement/${statementId}/pdf`, { responseType: 'blob' });
      return data;
    },
  });
}

export function useStatementHistory() {
  return useQuery({
    queryKey: ['statement-history'],
    queryFn: () => fetchOne<{ items: { id: number; statement_type: string; start_date: string; end_date: string; generated_at: string }[] }>('/payments/statement/history'),
  });
}

// ---------------------------------------------------------------------------
// Credit Summary & Applications
// ---------------------------------------------------------------------------

export function useCreditSummary() {
  return useQuery({
    queryKey: ['credit-summary'],
    queryFn: () => fetchOne<CreditSummary>('/customers/me/credit-summary'),
  });
}

export function useCreditApplications(status?: string) {
  return useQuery({
    queryKey: ['credit-applications', status],
    queryFn: () => fetchOne<{ items: CreditApplicationData[]; total: number }>('/customers/me/credit-applications', status ? { status } : undefined),
  });
}

export function useCreditApplication(id: number) {
  return useQuery({
    queryKey: ['credit-applications', id],
    queryFn: () => fetchOne<CreditApplicationData>(`/customers/me/credit-applications/${id}`),
    enabled: id > 0,
  });
}

export function useSubmitCreditApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { requested_amount: number; reason: string; business_justification?: string }) =>
      api.post('/customers/me/credit-applications', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['credit-applications'] }),
  });
}

export function useUploadCreditDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, formData }: { id: number; formData: FormData }) =>
      api.post(`/customers/me/credit-applications/${id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['credit-applications'] }),
  });
}

export function useCancelCreditApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/customers/me/credit-applications/${id}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['credit-applications'] }),
  });
}

// ---------------------------------------------------------------------------
// Exemption Certificates (Tax Certs)
// ---------------------------------------------------------------------------

export function useExemptionCertificates() {
  return useQuery({
    queryKey: ['exemption-certs'],
    queryFn: () => fetchOne<ExemptionCertificateData[]>('/customers/me/exemption-certificates'),
  });
}

export function useUploadExemptionCert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { cert_number: string; state: string; issue_date: string; expiry_date?: string; business_type?: string }) =>
      api.post('/customers/me/exemption-certificates', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['exemption-certs'] }),
  });
}

export function useExpiringCertificates() {
  return useQuery({
    queryKey: ['expiring-certs'],
    queryFn: () => fetchOne<ExemptionCertificateData[]>('/customers/me/exemption-certificates/expiring'),
  });
}

// ---------------------------------------------------------------------------
// Contract Pricing
// ---------------------------------------------------------------------------

export function useActiveContracts(params?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['active-contracts', params],
    queryFn: () => fetchOne<{ items: ContractDetail[]; total: number }>('/contracts/my-contracts', params),
  });
}

export function useContractDetail(id: number) {
  return useQuery({
    queryKey: ['contract-detail', id],
    queryFn: () => fetchOne<ContractDetail>(`/contracts/my-contracts/${id}`),
    enabled: id > 0,
  });
}

// ---------------------------------------------------------------------------
// Future Prices
// ---------------------------------------------------------------------------

export function useCustomerFuturePrices(params?: { product_id?: number; limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['future-prices', params],
    queryFn: () => fetchOne<{ items: FuturePrice[]; total: number }>('/pricing/my-future-prices', params),
  });
}

// ---------------------------------------------------------------------------
// Price Changes
// ---------------------------------------------------------------------------

export function usePriceChanges(params?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['price-changes', params],
    queryFn: () => fetchOne<{ items: any[]; total: number }>('/pricing/price-changes', params),
  });
}

// ---------------------------------------------------------------------------
// Support Tickets
// ---------------------------------------------------------------------------

export function useSupportTickets(params?: { status?: string; limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['support-tickets', params],
    queryFn: () => fetchOne<{ items: SupportTicketSummary[]; total: number }>('/support/tickets', params),
  });
}

export function useSupportTicket(id: number) {
  return useQuery({
    queryKey: ['support-ticket', id],
    queryFn: () => fetchOne<SupportTicketDetail>(`/support/tickets/${id}`),
    enabled: id > 0,
  });
}

export function useCreateSupportTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { subject: string; category: string; priority: string; description: string }) =>
      api.post('/support/tickets', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support-tickets'] }),
  });
}

export function useRespondToTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, message }: { id: number; message: string }) =>
      api.post(`/support/tickets/${id}/respond`, { message }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support-ticket'] }),
  });
}

export function useUploadTicketAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, formData }: { id: number; formData: FormData }) =>
      api.post(`/support/tickets/${id}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support-ticket'] }),
  });
}

export function useRateSupportTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, rating, comment }: { id: number; rating: number; comment?: string }) =>
      api.post(`/support/tickets/${id}/rate`, { rating, comment }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support-ticket'] }),
  });
}

// ---------------------------------------------------------------------------
// Customer Documents
// ---------------------------------------------------------------------------

export function useCustomerDocuments(params?: { category?: string; search?: string; limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['customer-documents', params],
    queryFn: () => fetchOne<{ items: CustomerDoc[]; total: number }>('/documents', params),
  });
}

export function useDocumentCategories() {
  return useQuery({
    queryKey: ['document-categories'],
    queryFn: () => fetchOne<DocCategory[]>('/documents/categories'),
  });
}

export function useDownloadDocument() {
  return useMutation({
    mutationFn: async (docId: number) => {
      const { data } = await api.get(`/documents/${docId}/download`, { responseType: 'blob' });
      return data;
    },
  });
}

// ---------------------------------------------------------------------------
// Account Team
// ---------------------------------------------------------------------------

export function useAccountTeam() {
  return useQuery({
    queryKey: ['account-team'],
    queryFn: () => fetchOne<TeamMember[]>('/account-team'),
  });
}

export function useSendTeamMessage() {
  return useMutation({
    mutationFn: ({ contactId, ...data }: { contactId: number; subject: string; message: string }) =>
      api.post(`/account-team/${contactId}/message`, data),
  });
}

// ---------------------------------------------------------------------------
// Direct Deposit Self-Service
// ---------------------------------------------------------------------------

export function useMyDirectDeposit() {
  return useQuery({
    queryKey: ['my-direct-deposit'],
    queryFn: () => fetchOne<{ accounts: any[] }>('/payroll/my/direct-deposit'),
  });
}

export function useUpdateMyDirectDeposit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, any>) =>
      api.post('/payroll/my/direct-deposit', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-direct-deposit'] }),
  });
}

export function useDeleteMyDirectDeposit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ configId, current_password }: { configId: number; current_password: string }) =>
      api.delete(`/payroll/my/direct-deposit/${configId}`, { data: { current_password } }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-direct-deposit'] }),
  });
}

// Phase 3: Employee AI Assistant
export function usePayrollAIAssistant() {
  return useMutation({
    mutationFn: (data: { question: string }) =>
      api.post('/payroll/my/ai-assistant', data).then(r => r.data),
  });
}

// Wave 2 — Order Templates
// ---------------------------------------------------------------------------

export function useOrderTemplates() {
  return useQuery({
    queryKey: ['order-templates'],
    queryFn: () => fetchOne<{ templates: OrderTemplate[] }>('/order-templates'),
  });
}

export function useOrderTemplate(name: string) {
  return useQuery({
    queryKey: ['order-templates', name],
    queryFn: () => fetchOne<OrderTemplate>(`/order-templates/${encodeURIComponent(name)}`),
    enabled: !!name,
  });
}

export function useCreateOrderTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; items: { product_id: number; quantity: number }[] }) =>
      api.post('/order-templates', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order-templates'] }),
  });
}

export function useUpdateOrderTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, ...data }: { name: string; items: { product_id: number; quantity: number }[] }) =>
      api.put(`/order-templates/${encodeURIComponent(name)}`, { name, ...data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order-templates'] }),
  });
}

export function useDeleteOrderTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.delete(`/order-templates/${encodeURIComponent(name)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order-templates'] }),
  });
}

export function usePlaceOrderFromTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, delivery_date }: { name: string; delivery_date: string }) =>
      api.post(`/order-templates/${encodeURIComponent(name)}/place-order`, { delivery_date }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });
}

// ---------------------------------------------------------------------------
// Wave 2 — Par Levels
// ---------------------------------------------------------------------------

export function useParLevels() {
  return useQuery({
    queryKey: ['par-levels'],
    queryFn: () => fetchOne<{ par_levels: ParLevelConfig[] }>('/par-levels'),
  });
}

export function useSetParLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { product_id: number; par_level: number; reorder_qty?: number }) =>
      api.post('/par-levels', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['par-levels'] }),
  });
}

export function useRemoveParLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (productId: number) => api.delete(`/par-levels/${productId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['par-levels'] }),
  });
}

export function useCheckBelowPar() {
  return useQuery({
    queryKey: ['par-levels', 'check'],
    queryFn: () => fetchOne<{ below_par: ParCheckResult[] }>('/par-levels/check'),
    enabled: false,
  });
}

export function useCreateParOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { items: { product_id: number; quantity: number }[]; delivery_date: string }) =>
      api.post('/par-levels/create-order', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });
}

// ---------------------------------------------------------------------------
// Wave 2 — Delivery Calendar
// ---------------------------------------------------------------------------

export function useDeliveryCalendar(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['delivery-calendar', startDate, endDate],
    queryFn: () => fetchOne<{ events: DeliveryCalendarEvent[] }>('/delivery-calendar', { start_date: startDate, end_date: endDate }),
    enabled: !!startDate && !!endDate,
  });
}

export function useDeliveryWindows() {
  return useQuery({
    queryKey: ['delivery-calendar', 'windows'],
    queryFn: () => fetchOne<{ windows: unknown[] }>('/delivery-calendar/windows'),
  });
}

export function useRequestReschedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { order_id: number; new_date: string; notes?: string }) =>
      api.post('/delivery-calendar/reschedule', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['delivery-calendar'] }),
  });
}

// ---------------------------------------------------------------------------
// Wave 2 — Delivery Feedback
// ---------------------------------------------------------------------------

export function useSubmitDeliveryFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { order_id: number; overall_rating: number; product_ratings?: unknown[]; photo_urls?: string[]; comments?: string; issues?: Record<string, boolean> }) =>
      api.post('/delivery-feedback', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['delivery-feedback'] }),
  });
}

export function useDeliveryFeedbackHistory(limit = 20, offset = 0) {
  return useQuery({
    queryKey: ['delivery-feedback', limit, offset],
    queryFn: () => fetchOne<{ feedback: DeliveryFeedbackData[] }>('/delivery-feedback', { limit, offset }),
  });
}

export function useQualityTrends(months = 6) {
  return useQuery({
    queryKey: ['delivery-feedback', 'trends', months],
    queryFn: () => fetchOne<QualityTrend>('/delivery-feedback/trends', { months }),
  });
}

// ---------------------------------------------------------------------------
// Wave 2 — Claims & Returns
// ---------------------------------------------------------------------------

export function useClaims(status?: string) {
  return useQuery({
    queryKey: ['claims', status],
    queryFn: () => fetchOne<{ claims: ClaimSummary[] }>('/claims', { status }),
  });
}

export function useFileQualityClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { invoice_id?: number; claim_type: string; items?: unknown[]; photos?: string[]; amount: number; description?: string }) =>
      api.post('/claims/quality', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claims'] }),
  });
}

export function useFileReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { order_id: number; items: { product_id: number; quantity: number }[]; reason_code?: string; photos?: string[]; notes?: string }) =>
      api.post('/claims/return', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claims'] }),
  });
}

export function useClaimDetail(type: string, id: number) {
  return useQuery({
    queryKey: ['claims', type, id],
    queryFn: () => fetchOne<ClaimDetail>(`/claims/${type}/${id}`),
    enabled: !!type && !!id,
  });
}

// ---------------------------------------------------------------------------
// Wave 2 — Substitution Preferences
// ---------------------------------------------------------------------------

export function useSubstitutionPreferences() {
  return useQuery({
    queryKey: ['substitution-preferences'],
    queryFn: () => fetchOne<{ preferences: SubstitutionPref[] }>('/substitution-preferences'),
  });
}

export function useSetSubstitutionPref() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { product_id: number; allowed: boolean; acceptable_ids?: number[]; reject_ids?: number[]; organic_only?: boolean; max_price_pct?: number; notes?: string }) =>
      api.post('/substitution-preferences', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['substitution-preferences'] }),
  });
}

export function useRemoveSubstitutionPref() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (productId: number) => api.delete(`/substitution-preferences/${productId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['substitution-preferences'] }),
  });
}

export function useSubstitutionHistory(limit = 20) {
  return useQuery({
    queryKey: ['substitution-preferences', 'history', limit],
    queryFn: () => fetchOne<{ history: SubstitutionHistoryItem[] }>('/substitution-preferences/history', { limit }),
  });
}

// ---------------------------------------------------------------------------
// Wave 2 — Price Watch
// ---------------------------------------------------------------------------

export function usePriceWatches() {
  return useQuery({
    queryKey: ['price-watch'],
    queryFn: () => fetchOne<{ watches: PriceWatch[] }>('/price-watch'),
  });
}

export function useAddPriceWatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { product_id: number; watch_type: string; threshold_pct?: number }) =>
      api.post('/price-watch', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['price-watch'] }),
  });
}

export function useRemovePriceWatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (productId: number) => api.delete(`/price-watch/${productId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['price-watch'] }),
  });
}

export function usePriceWatchAlerts(unreadOnly = false) {
  return useQuery({
    queryKey: ['price-watch', 'alerts', unreadOnly],
    queryFn: () => fetchOne<{ alerts: PriceWatchAlertItem[] }>('/price-watch/alerts', { unread_only: unreadOnly }),
  });
}

export function useMarkPriceAlertRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId: number) => api.post(`/price-watch/alerts/${alertId}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['price-watch', 'alerts'] }),
  });
}

// ---------------------------------------------------------------------------
// Wave 2 — Volume Discounts
// ---------------------------------------------------------------------------

export function useVolumeTiers(productId: number) {
  return useQuery({
    queryKey: ['volume-discount', productId],
    queryFn: () => fetchOne<{ tiers: VolumeTier[] }>(`/volume-discount/${productId}`),
    enabled: !!productId,
  });
}

export function useCartNudges() {
  return useMutation({
    mutationFn: (data: { items: { product_id: number; quantity: number; product_name?: string }[] }) =>
      api.post<{ nudges: DiscountNudge[] }>('/volume-discount/cart-nudges', data),
  });
}

// ---------------------------------------------------------------------------
// Wave 2 — Price Comparison
// ---------------------------------------------------------------------------

export function usePriceComparison(productId: number, days = 90) {
  return useQuery({
    queryKey: ['pricing', 'compare', productId, days],
    queryFn: () => fetchOne<PriceComparisonData>(`/pricing/compare/${productId}`, { days }),
    enabled: !!productId,
  });
}

export function usePortfolioSavings() {
  return useQuery({
    queryKey: ['pricing', 'portfolio-savings'],
    queryFn: () => fetchOne<{ total_savings: number; total_market_value: number; savings_pct: number; products: unknown[] }>('/pricing/portfolio-savings'),
  });
}

// ---------------------------------------------------------------------------
// Wave 2 — Multi-Location
// ---------------------------------------------------------------------------

export function useLocationDashboard() {
  return useQuery({
    queryKey: ['multi-location', 'dashboard'],
    queryFn: () => fetchOne<{ locations: LocationSummary[] }>('/multi-location/dashboard'),
  });
}

export function useLocationOrders(addressId: number, limit = 20, offset = 0) {
  return useQuery({
    queryKey: ['multi-location', addressId, 'orders', limit, offset],
    queryFn: () => fetchOne<{ orders: unknown[] }>(`/multi-location/${addressId}/orders`, { limit, offset }),
    enabled: !!addressId,
  });
}

// ---------------------------------------------------------------------------
// Wave 2 — Consolidated Spend
// ---------------------------------------------------------------------------

export function useConsolidatedSpend(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['analytics', 'consolidated-spend', startDate, endDate],
    queryFn: () => fetchOne<ConsolidatedSpend>('/analytics/consolidated-spend', { start_date: startDate, end_date: endDate }),
  });
}

// ---------------------------------------------------------------------------
// Wave 2 — Order Guides (location-specific)
// ---------------------------------------------------------------------------

export function useOrderGuides(addressId?: number) {
  return useQuery({
    queryKey: ['order-guides', addressId],
    queryFn: () => fetchOne<{ guides: OrderTemplate[] }>('/order-guides', { address_id: addressId }),
  });
}

export function useCreateOrderGuide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; items: { product_id: number; quantity?: number }[]; address_id?: number }) =>
      api.post('/order-guides', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order-guides'] }),
  });
}

export function useDeleteOrderGuide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/order-guides/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order-guides'] }),
  });
}

export function useGuideToCart() {
  return useMutation({
    mutationFn: (id: number) => api.post(`/order-guides/${id}/to-cart`),
  });
}

// ---------------------------------------------------------------------------
// Wave 2 — Seasonal Calendar
// ---------------------------------------------------------------------------

export function useSeasonalCalendar(commodity?: string, region?: string) {
  return useQuery({
    queryKey: ['seasonality', 'calendar', commodity, region],
    queryFn: () => fetchOne<{ calendar: SeasonalCalendarEntry[] }>('/seasonality/calendar', { commodity, region }),
  });
}

export function usePreOrderWindows(commodity?: string) {
  return useQuery({
    queryKey: ['seasonality', 'pre-order-windows', commodity],
    queryFn: () => fetchOne<{ windows: SourcingWindowInfo[] }>('/seasonality/pre-order-windows', { commodity }),
  });
}

// ---------------------------------------------------------------------------
// Wave 2 — Shortage Alerts
// ---------------------------------------------------------------------------

export function useShortageAlerts(status?: string) {
  return useQuery({
    queryKey: ['shortage-alerts', status],
    queryFn: () => fetchOne<{ alerts: ShortageAlertData[] }>('/shortage-alerts', { status }),
  });
}

export function useAcknowledgeShortageAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId: number) => api.post(`/shortage-alerts/${alertId}/acknowledge`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shortage-alerts'] }),
  });
}

export function useAcceptShortageSubstitute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ alertId, substitute_product_id }: { alertId: number; substitute_product_id: number }) =>
      api.post(`/shortage-alerts/${alertId}/accept-substitute`, { substitute_product_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shortage-alerts'] }),
  });
}

export function useDismissShortageAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId: number) => api.post(`/shortage-alerts/${alertId}/dismiss`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shortage-alerts'] }),
  });
}

// ---------------------------------------------------------------------------
// Wave 2 — Live GPS Tracking
// ---------------------------------------------------------------------------

export function useLiveTracking(orderId: number) {
  return useQuery({
    queryKey: ['tracking', orderId, 'live'],
    queryFn: () => fetchOne<LiveTrackingData>(`/tracking/${orderId}/live`),
    enabled: !!orderId,
    refetchInterval: 30000,
  });
}

// ---------------------------------------------------------------------------
// Demand Intelligence
// ---------------------------------------------------------------------------

export function useSuggestedOrder() {
  return useQuery({
    queryKey: ['suggested-order'],
    queryFn: () => fetchOne<{ suggested_items: SuggestedItem[] }>('/demand-intelligence/suggested-order/me'),
  });
}

export function useOrderPatterns() {
  return useQuery({
    queryKey: ['order-patterns'],
    queryFn: () => fetchOne<{
      items: OrderPatternItem[];
      always_items: OrderPatternItem[];
      sometimes_items: OrderPatternItem[];
      occasional_items: OrderPatternItem[];
      avg_weekly_value: number;
      trend: number;
    }>('/demand-intelligence/customer-patterns/me'),
  });
}

// ---------------------------------------------------------------------------
// Sub-Users (Team Management)
// ---------------------------------------------------------------------------

export function useSubUsers() {
  return useQuery({
    queryKey: ['sub-users'],
    queryFn: () => fetchOne<{ items: SubUser[] }>('/account/sub-users'),
  });
}

export function useInviteSubUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { username: string; email: string; role: string; can_place_orders?: boolean; can_make_payments?: boolean }) =>
      api.post('/account/sub-users', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sub-users'] }),
  });
}

export function useUpdateSubUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; role?: string; can_place_orders?: boolean; can_make_payments?: boolean; is_active?: boolean }) =>
      api.patch(`/account/sub-users/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sub-users'] }),
  });
}

export function useDeleteSubUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/account/sub-users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sub-users'] }),
  });
}

// ---------------------------------------------------------------------------
// Order Approvals
// ---------------------------------------------------------------------------

export function usePendingApprovals() {
  return useQuery({
    queryKey: ['pending-approvals'],
    queryFn: () => fetchOne<{ items: PendingApproval[] }>('/orders/pending-approvals'),
  });
}

export function useApproveOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/orders/${id}/approve`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pending-approvals'] }),
  });
}

export function useRejectOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      api.post(`/orders/${id}/reject`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pending-approvals'] }),
  });
}

// ---------------------------------------------------------------------------
// FreshByte-surpass Portal Hooks
// ---------------------------------------------------------------------------

export function useProvisionalCosts() {
  return useQuery({
    queryKey: ['provisional-costs'],
    queryFn: () => fetchOne<{ items: any[] }>('/price-after-sale/provisional-costs'),
  });
}

export function useCustomerPriceList() {
  return useQuery({
    queryKey: ['customer-price-list'],
    queryFn: () => fetchOne<{ items: any[] }>('/price-sheets/active'),
  });
}

export function useBidResponses() {
  return useQuery({
    queryKey: ['bid-responses'],
    queryFn: () => fetchOne<{ items: any[] }>('/advanced-pricing/bids'),
  });
}

export function useDeliverySchedule() {
  return useQuery({
    queryKey: ['delivery-schedule'],
    queryFn: () => fetchOne<{ items: any[] }>('/dsd/routes'),
  });
}

export function useDeliveryHistory() {
  return useQuery({
    queryKey: ['delivery-history'],
    queryFn: () => fetchOne<{ items: any[] }>('/dsd/routes', { status: 'completed' }),
  });
}

// ---------------------------------------------------------------------------
// Alert Preferences (Notification Settings)
// ---------------------------------------------------------------------------

export function useAlertPreferences() {
  return useQuery({
    queryKey: ['alert-preferences'],
    queryFn: () => fetchOne<any>('/customers/me/alert-preferences'),
  });
}

export function useUpdateAlertPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, any>) =>
      api.put('/customers/me/alert-preferences', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alert-preferences'] }),
  });
}

export function useTestOutreachChannel() {
  return useMutation({
    mutationFn: () => api.post('/customers/me/outreach/test'),
  });
}

// Re-exports for backwards compatibility
// All hooks have been split into domain-specific files under ./api/
export * from './api';
// ==================================================================
//  AI Customer Automation Hooks
// ==================================================================

export function useAIAccountHealth() {
  return useQuery({
    queryKey: ['ai-account-health'],
    queryFn: () => fetchOne<any>('/ai-customer/account/health'),
    refetchInterval: 120_000,
  });
}

export function useAIAccountInsights() {
  return useQuery({
    queryKey: ['ai-account-insights'],
    queryFn: () => fetchOne<any>('/ai-customer/account/insights'),
  });
}

export function useAIProductSearch() {
  return useMutation({
    mutationFn: (body: { query: string; customer_id?: number }) =>
      api.post('/ai-customer/products/search', body).then(r => r.data),
  });
}

export function useAIMenuPlanner() {
  return useMutation({
    mutationFn: (body: {
      event_type: string;
      guest_count: number;
      budget?: number;
      dietary?: string[];
    }) => api.post('/ai-customer/products/menu-plan', body).then(r => r.data),
  });
}

export function useAIRecipeParser() {
  return useMutation({
    mutationFn: (body: { recipe_text: string; servings?: number }) =>
      api.post('/ai-customer/products/recipe-to-cart', body).then(r => r.data),
  });
}

export function useAIDeliveryRisk(orderId: number) {
  return useQuery({
    queryKey: ['ai-delivery-risk', orderId],
    queryFn: () =>
      fetchOne<any>(`/ai-customer/delivery/risk/${orderId}`),
    enabled: orderId > 0,
  });
}

export function useAIDeliveryPatterns() {
  return useQuery({
    queryKey: ['ai-delivery-patterns'],
    queryFn: () =>
      fetchOne<any>('/ai-customer/delivery/patterns'),
  });
}

export function useAIPricingSuggestions(productId: number) {
  return useQuery({
    queryKey: ['ai-pricing-suggestion', productId],
    queryFn: () =>
      fetchOne<any>(`/ai-customer/pricing/dynamic/${productId}`),
    enabled: productId > 0,
  });
}

export function useAIPriceSensitivity() {
  return useQuery({
    queryKey: ['ai-price-sensitivity'],
    queryFn: () =>
      fetchOne<any>('/ai-customer/pricing/sensitivity'),
  });
}

export function useAIPaymentRisk() {
  return useQuery({
    queryKey: ['ai-payment-risk'],
    queryFn: () =>
      fetchOne<any>('/ai-customer/payments/risk'),
  });
}

export function useAIPaymentOptimize() {
  return useQuery({
    queryKey: ['ai-payment-optimize'],
    queryFn: () =>
      fetchOne<any>('/ai-customer/payments/optimize'),
  });
}

export function useAIDocCompliance() {
  return useQuery({
    queryKey: ['ai-doc-compliance'],
    queryFn: () =>
      fetchOne<any>('/ai-customer/compliance/dashboard'),
  });
}

export function useAIClaimAlert() {
  return useQuery({
    queryKey: ['ai-claim-alert'],
    queryFn: () =>
      fetchOne<any>('/ai-customer/claims/alert'),
    refetchInterval: 300_000,
  });
}

export function useAIOrderModEvaluate() {
  return useMutation({
    mutationFn: (body: { order_id: number; changes: any[] }) =>
      api.post('/ai-customer/orders/evaluate-mod', body).then(r => r.data),
  });
}

export function useAIOrderAlternatives(orderId: number) {
  return useQuery({
    queryKey: ['ai-order-alternatives', orderId],
    queryFn: () =>
      fetchOne<any>(`/ai-customer/orders/alternatives/${orderId}`),
    enabled: orderId > 0,
  });
}

export function useAISmartReply() {
  return useMutation({
    mutationFn: (body: { customer_id: number; inbound_message: string }) =>
      api.post('/ai-customer/comms/smart-reply', body).then(r => r.data),
  });
}

// ---------------------------------------------------------------------------
// Address Edit / Delete
// ---------------------------------------------------------------------------

export function useUpdateAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number } & Omit<Address, 'id'>) =>
      api.put(`/customers/me/addresses/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addresses'] }),
  });
}

export function useDeleteAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/customers/me/addresses/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addresses'] }),
  });
}

// ---------------------------------------------------------------------------
// CRM Contacts
// ---------------------------------------------------------------------------

export function useContacts() {
  return useQuery({
    queryKey: ['contacts'],
    queryFn: () => fetchOne<CRMContact[]>('/customers/me/contacts'),
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<CRMContact, 'id'>) =>
      api.post('/customers/me/contacts', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts'] }),
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: CRMContact) =>
      api.put(`/customers/me/contacts/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts'] }),
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/customers/me/contacts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts'] }),
  });
}

// ---------------------------------------------------------------------------
// Credit Application (portal, requires auth)
// ---------------------------------------------------------------------------

export function useSubmitPortalCreditApp() {
  return useMutation({
    mutationFn: (data: PortalCreditApplication) =>
      api.post('/customers/me/credit-application', data),
  });
}

// ---------------------------------------------------------------------------
// Net Terms Change Request
// ---------------------------------------------------------------------------

export function useSubmitNetTermsRequest() {
  return useMutation({
    mutationFn: (data: NetTermsChangeRequest) =>
      api.post('/customers/me/net-terms-request', data),
  });
}

// ---------------------------------------------------------------------------
// Statement Cycle Change Request
// ---------------------------------------------------------------------------

export function useSubmitStatementCycleRequest() {
  return useMutation({
    mutationFn: (data: StatementCycleChangeRequest) =>
      api.post('/customers/me/statement-cycle-request', data),
  });
}

// ---------------------------------------------------------------------------
// Employment Application (public, no auth required)
// ---------------------------------------------------------------------------

export function useSubmitEmploymentApp() {
  return useMutation({
    mutationFn: (data: EmploymentApplication) =>
      api.post('/employment-application', data),
  });
}

// ---------------------------------------------------------------------------
// Customer Feedback Insights (portal view of operational intelligence)
// ---------------------------------------------------------------------------

export function useCustomerFeedbackInsights() {
  return useQuery({
    queryKey: ['customer-feedback-insights'],
    queryFn: () =>
      fetchOne<any>('/operational-feedback/customer/me/intelligence'),
  });
}

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export function useGenerateReport() {
  return useMutation({
    mutationFn: (data: { report_type: string; start_date: string; end_date: string; format: string }) =>
      api.post('/reports/generate', data),
  });
}

// ---------------------------------------------------------------------------
// Public Credit Application (no auth required)
// ---------------------------------------------------------------------------

export function useSubmitPublicCreditApp() {
  return useMutation({
    mutationFn: (data: any) =>
      api.post('/credit-application', data),
  });
}

// ---------------------------------------------------------------------------
// RMA / Returns
// ---------------------------------------------------------------------------

export function useRMAList(status?: string) {
  return useQuery({
    queryKey: ['rma', status],
    queryFn: () => fetchOne<any>('/rma', { status }),
  });
}

export function useRMADetail(id: number | null) {
  return useQuery({
    queryKey: ['rma', id],
    queryFn: () => fetchOne<any>(`/rma/${id}`),
    enabled: !!id,
  });
}

export function useCreateRMA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { return_type: string; notes?: string }) =>
      api.post('/rma', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rma'] }),
  });
}

// ---------------------------------------------------------------------------
// Sales Quotes
// ---------------------------------------------------------------------------

export function useQuotes(status?: string) {
  return useQuery({
    queryKey: ['sales-quotes', status],
    queryFn: () => fetchOne<any>('/sales-quotes', { status }),
  });
}

export function useQuoteDetail(id: number | null) {
  return useQuery({
    queryKey: ['sales-quotes', id],
    queryFn: () => fetchOne<any>(`/sales-quotes/${id}`),
    enabled: !!id,
  });
}

export function useAcceptQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/sales-quotes/${id}/accept`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-quotes'] }),
  });
}

export function useRejectQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/sales-quotes/${id}/reject`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-quotes'] }),
  });
}

export function useConvertQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/sales-quotes/${id}/convert`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-quotes'] }),
  });
}

// ---------------------------------------------------------------------------
// RFQ
// ---------------------------------------------------------------------------

export function useRFQList() {
  return useQuery({
    queryKey: ['rfq'],
    queryFn: () => fetchOne<any>('/rfq/rfqs'),
  });
}

export function useRFQDetail(id: number | null) {
  return useQuery({
    queryKey: ['rfq', id],
    queryFn: () => fetchOne<any>(`/rfq/rfqs/${id}`),
    enabled: !!id,
  });
}

export function useCreateRFQ() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { title: string; description?: string }) =>
      api.post('/rfq/rfqs', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rfq'] }),
  });
}

export function useAddRFQItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rfqId, data }: { rfqId: number; data: any }) =>
      api.post(`/rfq/rfqs/${rfqId}/items`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rfq'] }),
  });
}

// ---------------------------------------------------------------------------
// Warranty
// ---------------------------------------------------------------------------

export function useWarrantyRegistrations() {
  return useQuery({
    queryKey: ['warranty-registrations'],
    queryFn: () => fetchOne<any>('/warranty/registrations/expiring'),
  });
}

export function useWarrantyClaims() {
  return useQuery({
    queryKey: ['warranty-claims'],
    queryFn: () => fetchOne<any>('/warranty/claims'),
  });
}

export function useSubmitWarrantyClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { registration_id: number; claim_type: string; claimed_amount: number; description?: string }) =>
      api.post('/warranty/claims', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warranty-claims'] }),
  });
}

// ---------------------------------------------------------------------------
// SMS Notifications
// ---------------------------------------------------------------------------

export function useSMSConsent() {
  return useQuery({
    queryKey: ['sms-consent'],
    queryFn: () => fetchOne<any>('/sms/consent/me'),
  });
}

export function useOptInSMS() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      phone_number: string;
      consent_method?: string;
      consent_text?: string;
    }) =>
      api.post('/sms/consent/opt-in', {
        consent_method: 'web_form',
        ...data,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sms-consent'] }),
  });
}

export function useOptOutSMS() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/sms/consent/opt-out'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sms-consent'] }),
  });
}

export function useSMSDeliveryLogs() {
  return useQuery({
    queryKey: ['sms-delivery-logs'],
    queryFn: () => fetchOne<any>('/sms/delivery-logs'),
  });
}

// ---------------------------------------------------------------------------
// IoT Customer Sensors
// ---------------------------------------------------------------------------

export function useCustomerSensors() {
  return useQuery({
    queryKey: ['customer-sensors'],
    queryFn: () => fetchOne<any>('/iot/customer/sensors'),
  });
}

export function useSensorReadings(sensorId: string | null) {
  return useQuery({
    queryKey: ['sensor-readings', sensorId],
    queryFn: () => fetchOne<any>(`/iot/customer/sensors/${sensorId}/readings`),
    enabled: !!sensorId,
  });
}

export function useSensorAlerts() {
  return useQuery({
    queryKey: ['sensor-alerts'],
    queryFn: () => fetchOne<any>('/iot/customer/alerts'),
  });
}

export function useRegisterSensor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { sensor_id: string; location_name: string; sensor_type: string; min_temp: number; max_temp: number; alert_email?: string }) =>
      api.post('/iot/customer/sensors', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer-sensors'] }),
  });
}

// ---------------------------------------------------------------------------
// Order Intake Queue
// ---------------------------------------------------------------------------

export function useOrderIntakeQueue() {
  return useQuery({
    queryKey: ['order-intake-queue'],
    queryFn: () => fetchOne<any>('/order-intake/queue'),
  });
}

export function useOrderIntakeStats() {
  return useQuery({
    queryKey: ['order-intake-stats'],
    queryFn: () => fetchOne<any>('/order-intake/stats'),
  });
}

export function useApproveIntakeDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draftId: number) => api.post(`/order-intake/queue/${draftId}/approve`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order-intake-queue'] }),
  });
}

export function useRejectIntakeDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draftId: number) => api.post(`/order-intake/queue/${draftId}/reject`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['order-intake-queue'] }),
  });
}

// ---------------------------------------------------------------------------
// Voicemail Orders
// ---------------------------------------------------------------------------

export function useVoicemailOrders() {
  return useQuery({
    queryKey: ['voicemail-orders'],
    queryFn: () => fetchOne<any>('/voicemail/process'),
  });
}

export function useVoicemailDetail(id: number | null) {
  return useQuery({
    queryKey: ['voicemail', id],
    queryFn: () => fetchOne<any>(`/voicemail/process/${id}`),
    enabled: !!id,
  });
}

export function useVoicemailCallbacks() {
  return useQuery({
    queryKey: ['voicemail-callbacks'],
    queryFn: () => fetchOne<any>('/voicemail/callbacks'),
  });
}

export function useResolveCallback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId: number) => api.put(`/voicemail/callbacks/${alertId}/resolve`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['voicemail-callbacks'] }),
  });
}

// ---------------------------------------------------------------------------
// Voice Agent Calls
// ---------------------------------------------------------------------------

export function useVoiceCalls() {
  return useQuery({
    queryKey: ['voice-calls'],
    queryFn: () => fetchOne<any>('/voice-agent/calls'),
  });
}

export function useVoiceCallDetail(callSid: string | null) {
  return useQuery({
    queryKey: ['voice-calls', callSid],
    queryFn: () => fetchOne<any>(`/voice-agent/calls/${callSid}`),
    enabled: !!callSid,
  });
}

export function useVoiceMetrics() {
  return useQuery({
    queryKey: ['voice-metrics'],
    queryFn: () => fetchOne<any>('/voice-agent/metrics'),
  });
}

// ---------------------------------------------------------------------------
// E-Commerce
// ---------------------------------------------------------------------------

export function useECommerceChannels() {
  return useQuery({
    queryKey: ['ecommerce-channels'],
    queryFn: () => fetchOne<any>('/ecommerce/channels'),
  });
}

export function useECommerceOrders(channelId?: number) {
  return useQuery({
    queryKey: ['ecommerce-orders', channelId],
    queryFn: () => fetchOne<any>('/ecommerce/orders', { channel_id: channelId }),
  });
}

// ---------------------------------------------------------------------------
// Customer Satisfaction
// ---------------------------------------------------------------------------

export function useSatisfactionScore() {
  return useQuery({
    queryKey: ['satisfaction-score'],
    queryFn: () => fetchOne<any>('/satisfaction/scores/me'),
  });
}

export function useSatisfactionHistory() {
  return useQuery({
    queryKey: ['satisfaction-history'],
    queryFn: () => fetchOne<any>('/satisfaction/scores'),
  });
}

export function useSatisfactionActions() {
  return useQuery({
    queryKey: ['satisfaction-actions'],
    queryFn: () => fetchOne<any>('/satisfaction/actions'),
  });
}

// ---------------------------------------------------------------------------
// CRM Activities (customer view)
// ---------------------------------------------------------------------------

export function useMyActivities(status?: string) {
  return useQuery({
    queryKey: ['my-activities', status],
    queryFn: () => fetchOne<any>('/crm/activities', { status }),
  });
}

export function useActivityDetail(id: number | null) {
  return useQuery({
    queryKey: ['activity', id],
    queryFn: () => fetchOne<any>(`/crm/activities/${id}`),
    enabled: !!id,
  });
}

// ---------------------------------------------------------------------------
// Social Media Content
// ---------------------------------------------------------------------------

export function useSocialPosts() {
  return useQuery({
    queryKey: ['social-posts'],
    queryFn: () => fetchOne<any>('/marketing/social-media/posts'),
  });
}

export function useSocialCampaigns() {
  return useQuery({
    queryKey: ['social-campaigns'],
    queryFn: () => fetchOne<any>('/marketing/social-media/campaigns'),
  });
}

// ---------------------------------------------------------------------------
// Smart Substitutions
// ---------------------------------------------------------------------------

export function useSubstitutionSuggestions(productId: number | null) {
  return useQuery({
    queryKey: ['substitution-suggestions', productId],
    queryFn: () => fetchOne<any>(`/substitutions/${productId}`),
    enabled: !!productId,
  });
}

export function useAcceptSubstitution() {
  return useMutation({
    mutationFn: (data: { original_product_id: number; substitute_product_id: number }) =>
      api.post('/substitutions/accept', data),
  });
}

// ---------------------------------------------------------------------------
// School Compliance - Buy America
// ---------------------------------------------------------------------------

export function useBuyAmericaCompliance() {
  return useQuery({
    queryKey: ['buy-america-compliance'],
    queryFn: () => fetchOne<any>('/compliance/buy-america'),
  });
}

export function useBuyAmericaWaivers() {
  return useQuery({
    queryKey: ['buy-america-waivers'],
    queryFn: () => fetchOne<any>('/compliance/buy-america/waivers'),
  });
}

// ---------------------------------------------------------------------------
// School Compliance - FFVP
// ---------------------------------------------------------------------------

export function useFFVPSummary() {
  return useQuery({
    queryKey: ['ffvp-summary'],
    queryFn: () => fetchOne<any>('/compliance/ffvp/summary'),
  });
}

export function useFFVPOrders() {
  return useQuery({
    queryKey: ['ffvp-orders'],
    queryFn: () => fetchOne<any>('/compliance/ffvp/orders'),
  });
}

// ---------------------------------------------------------------------------
// School Compliance - Sales Analysis
// ---------------------------------------------------------------------------

export function useSchoolSalesAnalysis() {
  return useQuery({
    queryKey: ['school-sales-analysis'],
    queryFn: () => fetchOne<any>('/compliance/school-sales/analysis'),
  });
}

export function useSchoolSalesByCategory() {
  return useQuery({
    queryKey: ['school-sales-by-category'],
    queryFn: () => fetchOne<any>('/compliance/school-sales/categories'),
  });
}
